import { useEffect, useState } from "react";
import { BreadCrum } from "../../common";
import CustomBreadcrumb from "../../common/CustomBreadcrumb";
import { DatePicker, Radio, Select, TimePicker } from "antd";
import dayjs from "dayjs";
import { useNavigate, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import Dragger from "../Dragger/Dragger";
import { useDispatch, useSelector } from "react-redux";
import {
  updateEmailTemplateAction,
  updateEmailTemplateHandler,
} from "../../redux/action/updateEmailTemplate";
import { useCommonMessage } from "../../common/CommonMessage";
import Loader from "../Loder/loader";
import { handleKeyPressSpace } from "../../common/CommonFunctions";
import dateicon from "../../assets/dateicon.svg";
import timeicon from "../../assets/timeicon.svg";
import {
  useAdminPermissions,
  VIEW_ONLY_EMPTY_MESSAGE,
  MODULE_ACCESS_DENIED_MESSAGE,
  resolveModuleAccessDeniedRoute,
} from "../../hooks/useAdminPermissions";

const dayMap = {
  MON: "Monday",
  TUE: "Tuesday",
  WED: "Wednesday",
  THU: "Thursday",
  FRI: "Friday",
  SAT: "Saturday",
  SUN: "Sunday",
};

const resolveTemplateType = (data) => {
  const hasSms = Boolean(data?.sms || data?.smsSpanish);
  const hasEmail = Boolean(data?.emailBody || data?.emailBodySpanish);

  if (hasSms && !hasEmail) {
    return "sms";
  }

  if (hasEmail && !hasSms) {
    return "email";
  }

  return hasSms ? "sms" : "email";
};

const QceTemplateViewPanel = ({
  manageData,
  scheduleTypes,
  week,
  day,
  date,
  notificationType,
  language,
  smsContent,
}) => {
  const viewTemplateLabel = notificationType === "sms" ? "SMS" : "Email";
  const viewLanguageLabel = language === "english" ? "English" : "Spanish";
  const viewDescription =
    notificationType === "sms"
      ? smsContent
      : language === "english"
        ? manageData?.emailBody
        : manageData?.emailBodySpanish;

  return (
    <>
      <div className="sectionpadding mb-30">
        <p className="mb-0 heedprodct">Manage Template</p>
        <hr />
        <div className="notification-view">
          <div className="notification-view-row">
            <div className="notification-view-field">
              <p className="notification-view-label">Year</p>
              <p className="notification-view-value">All</p>
            </div>
            <div className="notification-view-field">
              <p className="notification-view-label">Month</p>
              <p className="notification-view-value">All</p>
            </div>
          </div>
          <hr className="notification-view-divider" />
          <div className="notification-view-row">
            {scheduleTypes === "WEEKLY" && (
              <div className="notification-view-field">
                <p className="notification-view-label">Select Week*</p>
                <p className="notification-view-value">
                  {week ? `Week ${week}` : "-"}
                </p>
              </div>
            )}
            {scheduleTypes === "WEEKLY" && (
              <div className="notification-view-field">
                <p className="notification-view-label">Select Day*</p>
                <p className="notification-view-value">{day || "-"}</p>
              </div>
            )}
            {scheduleTypes === "MONTHLY" && (
              <div className="notification-view-field">
                <p className="notification-view-label">
                  Select Date of the Month*
                </p>
                <p className="notification-view-value">
                  {date || manageData?.dayOfMonth || "-"}
                </p>
              </div>
            )}
            <div className="notification-view-field">
              <p className="notification-view-label">Select Time</p>
              <p className="notification-view-value">
                {manageData?.timeOnly || "-"}
              </p>
            </div>
          </div>
          <hr className="notification-view-divider" />
          <div className="notification-view-field">
            <p className="notification-view-label">Template</p>
            <p className="notification-view-value">{viewTemplateLabel}</p>
          </div>
          <hr className="notification-view-divider" />
          <div className="notification-view-field">
            <p className="notification-view-label">Language</p>
            <p className="notification-view-value">{viewLanguageLabel}</p>
          </div>
        </div>
      </div>
      <div className="sectionpadding mb-30">
        <p className="mb-0 heedprodct">Description</p>
        <hr />
        {viewDescription ? (
          notificationType === "email" ? (
            <div
              className="release-document-view-html"
              dangerouslySetInnerHTML={{ __html: viewDescription }}
            />
          ) : (
            <p className="notification-view-value mb-0">{viewDescription}</p>
          )
        ) : (
          <p className="release-document-view-empty mb-0">
            {VIEW_ONLY_EMPTY_MESSAGE}
          </p>
        )}
      </div>
    </>
  );
};

const ManageQceTemplate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const messageApi = useCommonMessage();
  const { hasEmailTemplateViewOnly, isPending: permissionsPending, hasPermission, isSuperAdmin, PERMISSIONS, defaultRoute } =
    useAdminPermissions();
  const canViewEmailTemplate =
    isSuperAdmin || hasPermission(PERMISSIONS.EMAIL_TEMPLATE_VIEW);
  const canEditEmailTemplate =
    isSuperAdmin || hasPermission(PERMISSIONS.EMAIL_TEMPLATE_EDIT);
  const [forceViewOnlyFromApi, setForceViewOnlyFromApi] = useState(false);
  const isViewOnly =
    hasEmailTemplateViewOnly || (forceViewOnlyFromApi && canViewEmailTemplate);
  const { manageData } = location.state || {};
  const [date, setDate] = useState(manageData?.dayOfMonth);
  const reverseDayMap = Object.fromEntries(
    Object.entries(dayMap).map(([k, v]) => [v, k]),
  );
  // Convert short format (e.g., MON) to full weekday name for initial state
  const initialDay = dayMap[manageData?.dayOfWeek] || manageData?.dayOfWeek;
  const [day, setDay] = useState(initialDay);
  const [hour, setHour] = useState(
    manageData?.timeOnly ? dayjs(manageData.timeOnly, "HH:mm") : null
  );
  const [notificationType, setNotificationType] = useState(() =>
    resolveTemplateType(manageData),
  );
  const [smsType, setSmsType] = useState("sms");
  const [language, setLanguage] = useState("english");
  const [week, setWeek] = useState(manageData?.weekOfMonth);
  const [smsContent, setSmsContent] = useState( manageData?.sms);
  const [showSmsContentError, setShowSmsContentError] = useState(false);
  const days = Array.from({ length: 31 }, (_, i) => i + 1);
    const daysOfWeek = [
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
      "Sunday"
    ];
  const scheduleTypes = manageData?.scheduleType;
  const weeks = Array.from({ length: 4 }, (_, i) => i + 2);
  const onChange = (e) => {
    setNotificationType(e.target.value);
    setSmsType(e.target.value);
    setShowSmsContentError(false);
  
  };
  const onChangeLanguage = (e) => {
    const lang = e.target.value;
    setLanguage(lang);
   
  };
useEffect(() => {
   if (language === "english") {
      setSmsContent(manageData?.sms);
      setSmsType("sms");
    } else {
      setSmsContent(manageData?.smsSpanish);
      setSmsType("smsSpanish");
    }
}, [ language, manageData]);
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      templateName: manageData?.templateName || "",
      subject: manageData?.emailSubject || "",
      body: manageData?.emailBody || "",
      title: "",
      language: "english",
      sms: manageData?.sms,
      timeOnly: manageData?.timeOnly,
    },

    onSubmit: (value) => {
      if (notificationType === "sms") {
        if (!smsContent) {
          setShowSmsContentError(true);
          return;
        } else {
          setShowSmsContentError(false);
        }
      }

        // Convert selected day (e.g., 'Monday') to API value (e.g., 'MON')
        let transformedDay = day;
        if (day && typeof day === 'string' && reverseDayMap[day]) {
          transformedDay = reverseDayMap[day];
        }
        const payload = {
          id: manageData?.id,
          templateName: value?.templateName,
          eventType: manageData?.eventType,
          weekOfMonth: week,
          enabled: true,
          dayOfMonth: date || manageData?.dayOfMonth,
          dayOfWeek: scheduleTypes === "WEEKLY" ? transformedDay : undefined,
          timeOnly: hour ? dayjs(hour).format("HH:mm") : null,
          timeZone: "America/Chicago",
          
          // modifiedAt: day? hour
          //     ? dayjs(day)
          //         .hour(dayjs(hour).hour())
          //         .minute(dayjs(hour).minute())
          //         .second(0)
          //         .millisecond(0)
          //         .toISOString()
          //     : dayjs(day).startOf("day").toISOString()
          //   : null,
          ...((smsType === "sms" && notificationType === "sms")
            ? {
                sms: smsContent,
                smsSpanish: manageData?.smsSpanish,
                emailBody: undefined,
                emailBodySpanish: undefined,
                emailSubject: undefined,
                emailSubjectSpanish: undefined,
              }
             : (smsType === "smsSpanish"  && notificationType === "sms")
            ? {
                sms:  manageData?.sms,
                smsSpanish: smsContent,
                emailBody: undefined,
                emailBodySpanish: undefined,
                emailSubject: undefined,
                emailSubjectSpanish: undefined,
              }
            : language === "english"
            ? {
                sms: undefined,
                smsSpanish: undefined,
                emailBody: value?.body,
                emailSubject: value?.subject || "",
                emailBodySpanish: manageData?.emailBodySpanish,
                emailSubjectSpanish: manageData?.emailSubjectSpanish,
              }
            : language === "spanish"
            ? {
                sms: undefined,
                smsSpanish: undefined,
                emailBody: manageData?.emailBody,
                emailSubject: manageData?.emailSubject,
                emailBodySpanish: value?.body || "",
                emailSubjectSpanish: manageData?.emailSubjectSpanish || "",
              }
            : {}),
        };
      dispatch(updateEmailTemplateHandler(payload, { skipForbiddenRedirect: true }));
    },
  });
  const updateEmailTemplateSelector = useSelector(
    (state) => state?.updateEmailTemplate
  );

  useEffect(() => {
    if (!permissionsPending && isViewOnly && !manageData?.id) {
      navigate("/qce-templates", { replace: true });
    }
  }, [isViewOnly, manageData?.id, navigate, permissionsPending]);

  useEffect(() => {
    if (updateEmailTemplateSelector?.data?.statusCode === 200) {
      messageApi.open({
        type: "success",
        content: updateEmailTemplateSelector?.data?.message,
      });
      dispatch(updateEmailTemplateAction.updateEmailTemplateReset());
      navigate("/qce-templates");
    } else if (updateEmailTemplateSelector?.statusCode === 405) {
      messageApi.open({
        type: "warning",
        content:
          updateEmailTemplateSelector?.message ||
          MODULE_ACCESS_DENIED_MESSAGE,
      });
      dispatch(updateEmailTemplateAction.updateEmailTemplateReset());
      if (manageData?.id && canViewEmailTemplate) {
        setForceViewOnlyFromApi(true);
      }
      const redirectRoute = resolveModuleAccessDeniedRoute({
        canView: canViewEmailTemplate,
        canEdit: canEditEmailTemplate,
        moduleRoute: "/qce-templates",
        defaultRoute,
      });
      if (redirectRoute) {
        navigate(redirectRoute, {
          replace: true,
          ...(canViewEmailTemplate ? { state: { forceViewOnly: true } } : {}),
        });
      }
    } else if (updateEmailTemplateSelector?.message) {
      messageApi.open({
        type: "error",
        content: updateEmailTemplateSelector?.message,
      });
      dispatch(updateEmailTemplateAction.updateEmailTemplateReset());
    }
  }, [
    updateEmailTemplateSelector,
    canViewEmailTemplate,
    canEditEmailTemplate,
    defaultRoute,
    dispatch,
    manageData?.id,
    messageApi,
    navigate,
  ]);

  return (
    <>
      {(updateEmailTemplateSelector?.isLoading || permissionsPending) && (
        <Loader loaderTransForm="loaderTransForm" />
      )}
      <div className="rightside">
        <div className="rightcontent">
          {isViewOnly && manageData?.id ? (
            <>
              <p>
                <strong>QCE Templates</strong>
              </p>
              <CustomBreadcrumb
                items={[
                  { text: BreadCrum.HOME },
                  {
                    text: BreadCrum.QCE_TEMPLATE,
                    onClick: () => navigate("/qce-templates"),
                  },
                  { text: "View QCE Templates", active: true },
                ]}
              />
              <QceTemplateViewPanel
                manageData={manageData}
                scheduleTypes={scheduleTypes}
                week={week}
                day={day}
                date={date}
                notificationType={notificationType}
                language={language}
                smsContent={smsContent}
              />
            </>
          ) : (
            <>
          <p>
            <strong>QCE Templates</strong>
          </p>

          <CustomBreadcrumb
            items={[
              { text: BreadCrum.HOME },
              {
                text: BreadCrum.QCE_TEMPLATE,
                onClick: () => navigate("/qce-templates"),
              },
              { text: BreadCrum.EDIT_QCE_TEMPLATE, active: true },
            ]}
          />

          <div className="sectionpadding mb-30">
            <div className="mb-0 heedprodct">Manage Template</div>
            <hr />

            <div className="d-xxl-flex align-items-center mb-30 login-form">
              {/* Year */}
              <div className="noicon  pe-xxl-2 me-xxl-2 mb-xxl-0 mb-3 selecttype selecttype">
                <div className="labelp">Year</div>
                <input
                  type="text"
                  name=""
                  id=""
                  value="All"
                  className=" form-control"
                  disabled
                />
              </div>

              {/* Month */}
              <div className="noicon  pe-xxl-2 me-xxl-2 mb-xxl-0 mb-3 selecttype selecttype">
                <div className="labelp">Month</div>
                <input
                  type="text"
                  name=""
                  id=""
                  value="All"
                  className=" form-control"
                  disabled
                />
              </div>
              {/* Week */}
              {scheduleTypes === "WEEKLY" && (
                <div
                  className="noicon visibleicon pe-xxl-2 me-xxl-2 mb-xxl-0 mb-3 selecttype selecttype"
                  style={{ position: "relative" }}
                >
                  <div className="labelp">Select Week*</div>

                  <Select
                    placeholder="Select week"
                    className="w-100"
                    value={week}
                    onChange={(val) => setWeek(val)}
                  >
                    {weeks.map((week) => (
                      <Select.Option key={week} value={week}>
                        Week {week}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              )}
            </div>
            <div className="d-xxl-flex align-items-center mb-30">
              {/* Day */}
              {scheduleTypes === "WEEKLY" && (
                <div
                  className="noicon visibleicon pe-xxl-2 me-xxl-2 mb-xxl-0 mb-3 selecttype selecttype"
                  style={{ position: "relative" }}
                >
                  <div className="labelp">Select Day*</div>

                  <Select
                    placeholder="Select day"
                    className="w-100"
                    value={day}
                    onChange={(val) => setDay(val)}
                  >
                    {/* <Select placeholder="Select day" className="w-100" value={day} onChange={(val) => setDay(val)} suffixIcon={<img src={dateicon} alt="date icon" />}> */}
                    {daysOfWeek.map((weekday) => (
                      <Select.Option key={weekday} value={weekday}>
                        {weekday}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              )}
              {scheduleTypes === "MONTHLY" && (
                <div
                  className="noicon visibleicon pe-xxl-2 me-xxl-2 mb-xxl-0 mb-3 selecttype selecttype"
                  style={{ position: "relative" }}
                >
                  <div className="labelp">Select Date of the Month*</div>

                  <Select
                    placeholder="Select Date"
                    className="w-100"
                    value={date}
                    onChange={(val) => setDate(val)}
                  >
                    {days.map((day) => (
                      <Select.Option key={day} value={day}>
                        {day}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              )}
              {/* disabledDate={current => current && current < dayjs().startOf('day')} */}

              {/* Hour */}
              <div className=" noicon visibleicon pe-xxl-2 me-xxl-2 mb-xxl-0 mb-3 selecttype selecttype">
                <div className="labelp">Select Time*</div>
                <TimePicker
                  value={hour}
                  onChange={(val) => setHour(val)}
                  format="HH:mm"
                  placeholder="Select Hour and Minute"
                  style={{ width: "100%" }}
                  suffixIcon={<img src={timeicon} alt="time icon" />}
                />
              </div>
            </div>

            <hr className="mb-30" />
            <div className="labelp mb-20">Select a Template to edit</div>
            <div className="mb-30">
              <Radio.Group
                onChange={onChange}
                value={notificationType}
                options={[
                  { value: "sms", label: "SMS" },
                  { value: "email", label: "Email" },
                ]}
              />
            </div>
            <hr className="mb-30" />
            <div className="labelp mb-20">Select Language</div>
            <Radio.Group
              onChange={onChangeLanguage}
              value={language}
              options={[
                { value: "english", label: "English" },
                { value: "spanish", label: "Spanish" },
              ]}
            />
          </div>
          {notificationType === "sms" ? (
            <div className="sectionpadding mb-30">
              <div className="labelp">Description*</div>
              <textarea
                className="form-control"
                placeholder="Description"
                rows={4}
                value={smsContent}
                onChange={(e) => {
                  setSmsContent(e.target.value);
                  setShowSmsContentError(false);
                }}
                maxLength={200}
                onKeyDown={handleKeyPressSpace}
              ></textarea>
              {showSmsContentError && (
                <div className="error text-danger">
                  Description is required!
                </div>
              )}
            </div>
          ) : (
            <Dragger
              onlyHtmlEditer={true}
              initialHtml={
                language === "english"
                  ? manageData?.emailBody
                  : manageData?.emailBodySpanish
              }
              initialHtmlSpanish={manageData?.emailBodySpanish}
              onContentChange={(val) => formik.setFieldValue("body", val)}
              onContentChangeSpanish={(val) =>
                formik.setFieldValue("bodySpanish", val)
              }
              hideSaveButton={true}
            />
          )}
          <form onSubmit={formik.handleSubmit}>
            <div className="text-end bgt">
              <button
                type="submit"
                className="sbg border-0 py3 fw-18 gbo continuebtn savebtn"
              >
                Save
              </button>
            </div>
          </form>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ManageQceTemplate;
